import React from 'react';
import * as Sentry from '@sentry/browser';

const extractElementLocationData = (
  elementRef: React.RefObject<HTMLElement>,
): { classNames: string[]; dataRoles: string[] } => {
  const classNames: string[] = [];
  const dataRoles: string[] = [];

  if (!elementRef.current) {
    return { classNames, dataRoles };
  }

  const lastClassName = (elementRef.current.className?.split(' ')?.filter(Boolean) || []).pop();

  if (lastClassName) {
    classNames.push(lastClassName);
  }

  let currentElement: HTMLElement | null = elementRef.current.parentElement;

  while (currentElement && classNames.length < 10) {
    if (currentElement.className && !currentElement.className.startsWith('n--')) {
      const elementClasses = currentElement.className?.split(' ').filter(Boolean) || [];

      if (elementClasses[0]) {
        classNames.push(elementClasses[0]);
      }
    }

    const dataRole = currentElement.getAttribute('data-role');

    if (dataRole) {
      dataRoles.push(dataRole);
    }

    currentElement = currentElement.parentElement;
  }

  return { classNames, dataRoles };
};

const LOADING_THRESHOLD = 5_000;

export function useLoadersTracingRef<T extends HTMLElement>(visible: boolean) {
  const loaderRef = React.useRef<T>(null);

  React.useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    if (visible) {
      timeoutId = setTimeout(() => {
        const extra = extractElementLocationData(loaderRef);

        Sentry.withScope((scope) => {
          scope.setExtras(extra);
          scope.setFingerprint(['{{ default }}', Date.now().toString()]);

          Sentry.captureMessage(`LoadingIndicator: active for more than ${LOADING_THRESHOLD}ms`);
        });
      }, LOADING_THRESHOLD);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [visible]);

  return loaderRef;
}
